import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import type { SortingState } from "@tanstack/react-table";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  listToReceive,
  type ToReceiveSort,
} from "@/shared/api/purchasing";
import {
  queryErrorMessage,
  useAuthRedirectOnQueryError,
} from "@/shared/query/query-error";
import { queryKeys } from "@/shared/query/keys";
import type { PartyFilter } from "@/modules/core/list-cell-actions";
import {
  AdminFilters,
  adminFilterControlClass,
} from "@/modules/shell/AdminFilters";
import { ActiveFilterChips } from "@/modules/shell/ActiveFilterChips";
import {
  readPartyFilter,
  readQ,
  setOrDelete,
  writePartyFilter, LIST_SEARCH_DEBOUNCE_MS } from "@/modules/shell/list-url-filters";
import { useListUrlParams } from "@/modules/shell/use-list-url-params";
import { DataTable } from "@/modules/shell/data-table";
import {
  DataTableChrome,
  DeskListFilterReset,
} from "@/modules/shell/DataTableChrome";
import { useDeskListFilterReset } from "@/modules/shell/use-desk-list-filter-reset";
import {
  ListSearchStatus,
  listSearchOnlinePending,
} from "@/modules/shell/ListSearchStatus";
import { DeskListHeader } from "@/modules/shell/DeskListHeader";
import { DeskSubNav } from "@/modules/shell/DeskSubNav";
import { purchasingModuleTabs } from "@/modules/shell/desk-module-tabs";
import { EmptyState } from "@/modules/shell/EmptyState";
import { PageFrame } from "@/modules/shell/PageFrame";
import { toReceiveColumns } from "./to-receive-columns";

const PAGE_SIZE = 50;
const SORTABLE: ToReceiveSort[] = [
  "number",
  "supplierName",
  "receive",
  "qtyReceived",
  "expectedOn",
];

function sortingToApi(sorting: SortingState): {
  sort: ToReceiveSort;
  sortDir: "asc" | "desc";
} {
  const first = sorting[0];
  if (first && SORTABLE.includes(first.id as ToReceiveSort)) {
    return {
      sort: first.id as ToReceiveSort,
      sortDir: first.desc ? "desc" : "asc",
    };
  }
  return { sort: "number", sortDir: "asc" };
}

export function ToReceivePage() {
  const { companySlug } = useParams();
  const { searchParams, patchParams } = useListUrlParams();
  const [query, setQuery] = useState(() => readQ(searchParams));
  const [debouncedQ, setDebouncedQ] = useState(() => readQ(searchParams));
  const partyFilter = readPartyFilter(searchParams);
  const setPartyFilter = (next: PartyFilter | null) => {
    patchParams((p) => writePartyFilter(p, next));
  };
  const [sorting, setSorting] = useState<SortingState>([]);
  const { sort, sortDir } = sortingToApi(sorting);

  useEffect(() => {
    const t = window.setTimeout(() => {
      const q = query.trim();
      setDebouncedQ(q);
      patchParams((p) => setOrDelete(p, "q", q));
    }, LIST_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [query, patchParams]);

  const listQuery = useInfiniteQuery({
    queryKey: queryKeys.queues.toReceive(companySlug ?? "", {
      q: debouncedQ,
      limit: PAGE_SIZE,
      sort,
      sortDir,
    }),
    enabled: Boolean(companySlug),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      listToReceive(companySlug!, {
        q: debouncedQ || undefined,
        limit: PAGE_SIZE,
        cursor: pageParam,
        sort,
        sortDir,
        signal,
      }),
    getNextPageParam: (last) =>
      last.hasMore && last.nextCursor ? last.nextCursor : undefined,
  });
  const errorMsg = queryErrorMessage(listQuery.error, listQuery.isError);
  useAuthRedirectOnQueryError(errorMsg);

  const columns = useMemo(
    () =>
      companySlug
        ? toReceiveColumns(companySlug, { partyFilter, setPartyFilter })
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companySlug, partyFilter],
  );

  if (!companySlug) return null;

  const orders = listQuery.data?.pages.flatMap((p) => p.orders) ?? [];
  const visible = partyFilter
    ? orders.filter((o) => o.supplierId === partyFilter.id)
    : orders;
  const total = listQuery.data?.pages[0]?.total;
  const loading = listQuery.isPending;
  const error = errorMsg && errorMsg !== "Not signed in" ? errorMsg : null;
  const filtersActive = Boolean(query.trim()) || Boolean(partyFilter);
  const filterReset = useDeskListFilterReset({
    patchParams,
    setQuery: setQuery,
    canResetToDefault: filtersActive,
    canClearAll: filtersActive,
  });
  const searchingOnline = listSearchOnlinePending({
    query,
    debouncedQ,
    isFetching: listQuery.isFetching,
  });

  return (
    <PageFrame>
      <DataTableChrome>
      <DeskListFilterReset {...filterReset} />
      <DeskListHeader
        title="To receive"
        description="Open purchase orders with unreceived qty"
        tabs={
          <DeskSubNav
            placement="header"
            items={purchasingModuleTabs(companySlug)}
            aria-label="Purchasing views"
          />
        }
      />

      <AdminFilters>
        <input
          className={`${adminFilterControlClass} min-w-[12rem] flex-1`}
          placeholder="Search number or supplier…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={loading}
        />
        <ActiveFilterChips
          chips={
            partyFilter
              ? [
                  {
                    key: "party",
                    label: `Supplier: ${partyFilter.name}`,
                    onClear: () => setPartyFilter(null),
                  },
                ]
              : []
          }
        />
      </AdminFilters>
      <ListSearchStatus online searching={searchingOnline} />
      {visible.length > 0 && total != null ? (
        <p className="mt-2 text-xs text-[var(--color-muted)]">
          Showing {visible.length}
          {total > orders.length ? ` · loaded ${orders.length} of ${total}` : ""}
        </p>
      ) : null}

      {error && (
        <Alert variant="destructive" className="mt-4">
          {error}
        </Alert>
      )}
      {loading && (
        <Alert variant="muted" className="mt-8">
          Loading receipts…
        </Alert>
      )}
      {!loading && visible.length === 0 && !error && (
        <div className="mt-8">
          <EmptyState
            title={filtersActive ? "No match" : "All clear"}
            description={
              filtersActive
                ? "Try clearing search or supplier filter."
                : "Nothing to receive right now."
            }
            action={
              filtersActive ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setQuery("");
                    setPartyFilter(null);
                  }}
                >
                  Clear filters
                </Button>
              ) : (
                <Button asChild variant="outline" size="sm">
                  <Link
                    to={`/c/${companySlug}/purchasing/orders/new`}
                    className="no-underline"
                  >
                    New PO
                  </Link>
                </Button>
              )
            }
          />
        </div>
      )}
      {visible.length > 0 ? (
        <>
          <DataTable
            onRefresh={() => void listQuery.refetch()}
            listId="purchasing-to-receive"
            companySlug={companySlug}
            columns={columns}
            data={visible}
            getRowId={(r) => r.id}
            sorting={sorting}
            onSortingChange={setSorting}
          />
          {listQuery.hasNextPage ? (
            <div className="mt-4 flex justify-center">
              <Button
                type="button"
                variant="outline"
                disabled={listQuery.isFetchingNextPage}
                pending={listQuery.isFetchingNextPage}
                pendingLabel="Loading more…"
                onClick={() => void listQuery.fetchNextPage()}
              >
                Load more
              </Button>
            </div>
          ) : null}
        </>
      ) : null}
      <p className="mt-8 text-sm">
        <Link
          to={`/c/${companySlug}/purchasing/orders`}
          className="no-underline"
        >
          ← Purchase orders
        </Link>
        {" · "}
        <Link
          to={`/c/${companySlug}/purchasing/to-pay`}
          className="no-underline"
        >
          To pay
        </Link>
      </p>
          </DataTableChrome>
    </PageFrame>
  );
}
